import { useState, useRef, useEffect, useContext, act } from "react";
import { Resizable } from "re-resizable";
import "../../../../styles/Widgets/GeneralWidget.css";
import "../../../../styles/Widgets/LiquidLevel/VerticalCylinderTankWidget.css";
import { useParams } from "react-router-dom";
import { thingsBoardMicroserviceUrl } from "../../../../microservices/thingsboard_microservice";
import JwtDecodeService from "../../../../services/JwtDecodeService";
import { AppContext } from "../../../AppProvider";

const VerticalCylinderTankWidget = ({ DashboardContext, widget, setWidgets, bounds, setDraggingOrResizing, MOBILE_COMPUTER_SCREEN_BOUNDARY }) => {
    const {
        editMode,setActiveDashboardState,
        activeEditWidget,setActiveEditWidget
    } = useContext(DashboardContext);
    const {user,customerIotProfile,logout} = useContext(AppContext);
    const {tenantId,customerAdminId,customerUserId} = useParams();
    const [device,setDevice] = useState(null);
    const [lastUpdate,setLastUpdate] = useState(null);
    const [tankInactive,setTankInactive] = useState(false);
    
    const fetchDeviceById = async(deviceId) =>{
        try{
            const decodedAccessToken = JwtDecodeService(user?.access_token);
            const defaultAuthority = decodedAccessToken && decodedAccessToken["user-authorities"].length > 0? decodedAccessToken["user-authorities"][0] : 'NONE';
            let url;
            if(defaultAuthority === 'TENANT_ADMIN') url = `${thingsBoardMicroserviceUrl}/tenants/profiles/${tenantId}/devices/${deviceId}`;
            else if(defaultAuthority === 'CUSTOMER' && customerIotProfile?.role === 'ADMIN') url = `${thingsBoardMicroserviceUrl}/customers/users/admin/${customerAdminId}/devices/${deviceId}`;
            else if(defaultAuthority === 'CUSTOMER' && customerIotProfile?.role === 'USER') url = `${thingsBoardMicroserviceUrl}/customers/users/user/${customerUserId}/devices/${deviceId}`;
            const response = await fetch(url,{
                method:'GET',
                headers:{
                    'Authorization':`Bearer ${user.access_token}`,
                    'Content-Type':'application/json'
                }
            });
            if(response.ok){
                const responseData = await response.json();
                setDevice(responseData);
            }
            else if(Number(response.status) === 401) logout();
            else throw new Error(`Response code: ${response.status}`);
        }
        catch(error){
            console.error(error);
        }
    };
    const extractRefreshRate = () =>{
        const refreshRateString = widget?.config?.dataSource?.refreshRate;
        if (!refreshRateString) return 5000;
        const regex = /(\d+)\s*(second|seconds|minute|minutes)/i;
        const match = refreshRateString.match(regex);
        if (!match) return 5000;
        const value = parseInt(match[1], 10);
        const unit = match[2].toLowerCase();
        const seconds = unit.startsWith("minute") ? value * 60 : unit.startsWith("second") ? value : 5;
        return seconds * 1000;
    };
    const formatLastUpdateDisplay= () =>{
        if(!lastUpdate) return 'Indefinite';
        const lastUpdateTimeDifference = Date.now() - lastUpdate;
        const seconds = Math.floor(lastUpdateTimeDifference / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);
        if (seconds < 60) return 'Just now';
        if (minutes < 2) return `${minutes} minute ago`;
        if (minutes < 3) return `${minutes} minutes ago`;
        if (minutes < 30) return `${minutes} minutes ago`;
        if (hours < 2) return '1 hour ago';
        if (hours < 24) return `${hours} hours ago`;
        if (days < 2) return '1 day ago';
        if (days < 3) return `${days} days ago`;
        const lastUpdateDate = new Date(lastUpdate);
        const formattedDate = `${lastUpdateDate.getMonth() + 1}/${lastUpdateDate.getDate()}/${lastUpdateDate.getFullYear()}`;
        return `${formattedDate}`;
    };
    //region Widget General features
    const { gridCellSize } = bounds;
    const [resizing,setResizing] = useState(false);
    const [dragging,setDragging] = useState(false);
    const [newWidgetSize,setNewWidgetSize] = useState({ width: widget.sizeX, height: widget.sizeY });
    const [widgetPosition, setWidgetPosition] = useState({ col: widget.col, row: widget.row });
    const [widgetSize, setWidgetSize] = useState({ width: widget.sizeX, height: widget.sizeY });
    const resizableRef = useRef(null);
    const mouseMoveRef = useRef(null);
    const mouseUpRef = useRef(null);
    const position = { 
        x: widgetPosition.col * (gridCellSize + bounds.colGap) + bounds.colGap,
        y: widgetPosition.row * (gridCellSize + bounds.rowGap) + bounds.rowGap
    };
    const size = { 
        width: widgetSize.width * gridCellSize,
        height: widgetSize.height * gridCellSize
    };
    const screenIsMobileScreen = () =>{
        return bounds.width < MOBILE_COMPUTER_SCREEN_BOUNDARY;
    };
    const handleMouseDown = (e) => {
        if(!editMode || resizing || screenIsMobileScreen()) return;
        if(resizableRef.current?.resizableElement?.contains(e.target)) return;

        setDragging(true);
        const startX = e.clientX - position.x;
        const startY = e.clientY - position.y;

        const handleMouseMove = (e) => {
            let newCol = Math.round((e.clientX - startX) / gridCellSize);
            let newRow = Math.round((e.clientY - startY) / gridCellSize);

            newCol = Math.max(0, Math.min(Math.floor(bounds.width / gridCellSize) - widgetSize.width, newCol));
            newRow = Math.max(0, Math.min(Math.floor(bounds.height / gridCellSize) - widgetSize.height, newRow));

            setWidgetPosition({ col: newCol, row: newRow });
        };

        const handleMouseUp = () => {
            setDragging(false);
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };

        mouseMoveRef.current = handleMouseMove;
        mouseUpRef.current = handleMouseUp;
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
    };
    const handleResizing = (e,direction,ref) =>{
        const newWidth = ref.clientWidth;
        const newHeight = ref.clientHeight;
        setNewWidgetSize({
            width:newWidth,
            height:newHeight
        });
    };
    const exportWidget = () =>{
        const blob = new Blob([JSON.stringify(widget,null,2)],{type: "application/json"});
        const url = URL.createObjectURL(blob);
        const a = Object.assign(document.createElement("a"), {href: url, download: `${widget.type}_${widget?.config?.title}.json`});
        a.click();
        URL.revokeObjectURL(url);
    };
    const removeWidget = () =>{
        setActiveDashboardState(prev =>({
            ...prev,widgets:prev?.widgets?.filter(w => w.id !== widget?.id)
        }));
    };
    useEffect(() =>{
        setWidgetSize(prev => ({
            width: Math.round(prev.width),
            height: Math.round(prev.height),
        }));

        setWidgetPosition(prev => ({
            col: Math.round(prev.col),
            row: Math.round(prev.row),
        }));
    },[gridCellSize]);
    useEffect(() =>{
        setDraggingOrResizing(dragging || resizing);
    },[resizing,dragging]);
    useEffect(() =>{
        if(screenIsMobileScreen()) return;
        setNewWidgetSize({
            width:widgetSize.width * gridCellSize,
            height:widgetSize.height * gridCellSize
        });
        setWidgets((prevWidgets) => prevWidgets.map((prevWidget) =>(
            prevWidget.id === widget.id? {
                ...prevWidget,
                row: widgetPosition.row,
                col: widgetPosition.col,
                sizeX: widgetSize.width,
                sizeY: widgetSize.height
            }: prevWidget
        )));
    },[widgetPosition,widgetSize]);
    useEffect(() =>{
        fetchDeviceById(widget?.config?.dataSource?.deviceId);
        const intervalId = setInterval(() => {
            fetchDeviceById(widget?.config?.dataSource?.deviceId);
        },extractRefreshRate());
        return () => clearInterval(intervalId);
    },[]);
    useEffect(() =>{
        return () =>{
            window.removeEventListener("mouseup", mouseMoveRef.current);
            window.removeEventListener("mouseup",mouseUpRef.current);
        }
    },[]);
    useEffect(() =>{
        if(!device) return;
        const levelEntry = device?.latestTelemetry.find(item => item.key === widget?.config?.dataSource?.dataKey?.key);
        const deviceLevel = levelEntry ? levelEntry.value : null;
        const maximumLevel = widget?.config?.data?.maximumValue;
        if(!currentLevel || !maximumLevel || isNaN(Number(deviceLevel)) || isNaN(Number(maximumLevel))) setFillHeight(0);
        if(false);
        else{
            const lastUpdateTime = device?.latestTelemetry.find(item => item.key === widget?.config?.dataSource?.dataKey?.key)?.timeStamp;
            setLastUpdate(lastUpdateTime);
            let fillHeight = Math.min(deviceLevel/maximumLevel * 100,100);
            const useInactiveColor = ((Date.now() - lastUpdateTime) > widget?.config?.appearance?.inactivityThreshold) && widget?.config?.appearance?.applyInactivityEffects;
            if(fillHeight < (widget?.config?.data?.lowLevelThreshold ?? 10)) setFillColor(useInactiveColor? widget?.config?.appearance?.inactiveColor:widget?.config?.appearance?.lowLevelColor);
            else setFillColor(useInactiveColor? widget?.config?.appearance?.inactiveColor:widget?.config?.appearance?.highLevelColor);
            fillHeight = fillHeight < 7? 7:fillHeight;
            setFillHeight(fillHeight);
            setCurrentLevel(deviceLevel);
            setMaxLevel(maximumLevel);
            setTankColor(widget?.config?.appearance?.tankColor);
            setTankFillColor(widget?.config?.appearance?.backgroundColor);
        }
    },[device]);
    //endregion

    const [tankWidth,setTankWidth] = useState(100);
    const [tankHeight,setTankHeight] = useState(100);
    const [fillColor,setFillColor] = useState('blue');
    const [tankColor,setTankColor] = useState('#00008B');
    const [tankFillColor,setTankFillColor] = useState('#FFFFFF');
    const [currentLevel,setCurrentLevel] = useState(0);
    const [maxLevel,setMaxLevel] = useState(0);
    const [fillHeight,setFillHeight] = useState(10);
    const tankMargin = 2;
    const tankpadding = 8;
    const tankContainerRef = useRef(null);
    useEffect(() =>{
        if(tankContainerRef.current){
            setTankWidth(tankContainerRef.current.clientWidth - tankMargin * 2);
            setTankHeight(tankContainerRef.current.clientHeight - tankMargin * 2);
        }
    },[newWidgetSize]);
    return (
        <div className={`general-widget-parent-container ${editMode? (activeEditWidget?.id === widget?.id? 'selected-edit':activeEditWidget? 'active-edit':'passive-edit'):''}`}
            style={{
                position: "absolute",
                left: position.x,
                top: position.y,
                cursor: dragging ? "grabbing" : "default"
            }}
            onClick={() =>{
                if(!activeEditWidget) return;
                setActiveEditWidget(widget);
            }}
            onMouseDown={handleMouseDown}>
            <div className={`general-widget-active-edit-mask ${editMode && activeEditWidget && activeEditWidget?.id !== widget?.id? 'active':''}`}></div>
            <Resizable
                ref={resizableRef}
                size={size}
                minWidth={3 * gridCellSize}
                minHeight={3 * gridCellSize}
                enable={{ right: editMode && !activeEditWidget, bottom: editMode && !activeEditWidget, bottomRight: editMode && !activeEditWidget }}
                onResizeStart={(e) => {
                    e.stopPropagation();
                    setResizing(true);
                }}
                onResize={(e, direction, ref) =>handleResizing(e,direction,ref)}
                onResizeStop={(e,direction,ref) => {
                    setWidgetSize({
                        width: Math.round(ref.clientWidth / gridCellSize),
                        height: Math.round(ref.clientHeight / gridCellSize),
                    });
                    setResizing(false);
                }}>
                <div className="general-widget-container" style={{borderRadius:`${widget?.borderRadius}%`}} onMouseDown={handleMouseDown}>
                    <div className="general-widget-edit-controls-container"
                        style={{display:editMode && !activeEditWidget? 'flex':'none'}}>
                        <div onClick={() => setActiveEditWidget(widget)}>
                            <p className="edit-widget-p">&#9997;</p>
                        </div>
                        <div onClick={() => exportWidget()}>
                            <p className="export-widget-p">&#128229;</p>
                        </div>
                        <div onClick={() => removeWidget()}>
                            <p className="remove-widget-p">&#10006;</p>
                        </div>
                    </div>
                    <div className="vertical-cylinder-tank-widget-container">
                        <div className="vertical-cylinder-tank-widget-tool-tip"
                             style={{top:`${widgetSize?.height * (screenIsMobileScreen()? (widget?.mobileHeightFactor || 4)/2 : 12)}px`}}>
                            <div>
                                <p>Level <span>{currentLevel}</span></p>
                                <p>Last update <span>{formatLastUpdateDisplay()}</span></p>
                            </div>
                        </div>
                        <div className="vertical-cylinder-tank-widget-header">
                            <p style={{
                                visibility:widget?.config?.appearance?.showTitle? 'visible':'hidden',
                                color:widget?.config?.appearance?.titleColor}}>{widget?.config?.appearance?.title}</p>
                        </div>
                        <div className="vertical-cylinder-tank-widget-body">
                            <div className="vertical-cylinder-tank-widget-tank-container" ref={tankContainerRef}>
                                <svg>
                                    {/* Cylindrical body */}
                                    <rect 
                                        x={tankMargin}
                                        y={tankMargin + tankHeight * 0.28}
                                        width={tankWidth}
                                        height={tankHeight * 0.62}
                                        fill={tankFillColor}
                                        stroke={tankColor} 
                                        strokeWidth={2} 
                                    />
                                    {/* Top ellipse */}
                                    <ellipse
                                        cx={tankWidth / 2 + tankMargin}
                                        cy={tankMargin + tankHeight * 0.28}
                                        rx={tankWidth / 2}
                                        ry={tankHeight * 0.1}
                                        fill={tankFillColor}
                                        stroke={tankColor}
                                        strokeWidth={2}
                                    />
                                    {/* Top ellipse mask */}
                                    <rect
                                        x={0}
                                        y={0}
                                        width={tankWidth + (tankMargin * 2)}
                                        height={tankHeight * 0.28}
                                        fill={tankFillColor}
                                    />
                                    {/* Conical top */}
                                    <path
                                        d={`M ${(tankWidth / 2) - tankWidth / 10} ${tankHeight * 0.08} L ${tankMargin} ${tankMargin + tankHeight * 0.275} M ${(tankWidth / 2) + tankWidth / 10} ${tankHeight * 0.08} L ${tankWidth + tankMargin} ${tankMargin + tankHeight * 0.275}`}
                                        fill={tankFillColor} 
                                        stroke={tankColor} 
                                        strokeWidth="2"
                                    />
                                    {/* Conical top cover ellipse */}
                                    <ellipse
                                        cx={tankWidth / 2}
                                        cy={tankHeight * 0.08}
                                        rx={tankWidth / 10}
                                        ry={tankWidth * 0.02}
                                        fill={tankFillColor}
                                        stroke={tankColor}
                                        strokeWidth={2}
                                    />
                                    {/* Conical top cover ellipse mask */}
                                    <ellipse
                                        cx={tankWidth / 2}
                                        cy={tankHeight * 0.07}
                                        rx={tankWidth / 10}
                                        ry={tankHeight * 0.02 * tankWidth / tankHeight}
                                        fill={tankFillColor}
                                        stroke={tankFillColor}
                                        strokeWidth={2}
                                    />
                                    {/* Conical top cover lid left line*/}
                                    <line
                                        x1={(tankWidth / 2) - tankWidth / 10}
                                        y1={tankHeight * 0.04}
                                        x2={(tankWidth / 2) - tankWidth / 10}
                                        y2={tankHeight * 0.08}
                                        stroke={tankColor}
                                        strokeWidth={2}
                                    />
                                    {/* Conical top cover lid right line*/}
                                    <line
                                        x1={(tankWidth / 2) + tankWidth / 10}
                                        y1={tankHeight * 0.04}
                                        x2={(tankWidth / 2) + tankWidth / 10}
                                        y2={tankHeight * 0.08}
                                        stroke={tankColor}
                                        strokeWidth={2}
                                    />
                                    {/* Conical top cover lid ellipse */}
                                    <ellipse
                                        cx={tankWidth / 2}
                                        cy={tankHeight * 0.04}
                                        rx={tankWidth / 10}
                                        ry={tankWidth * 0.02}
                                        fill={tankFillColor}
                                        stroke={tankColor}
                                        strokeWidth={2}
                                    />
                                    {/* Bottom ellipse */}
                                    <ellipse
                                        cx={tankWidth / 2 + tankMargin}
                                        cy={tankHeight * 0.9}
                                        rx={tankWidth / 2}
                                        ry={tankHeight * 0.1}
                                        fill={tankFillColor}
                                        stroke={tankColor}
                                        strokeWidth={2}
                                    />
                                    {/* Bottom inner ellipse */}
                                    <ellipse
                                        cx={tankWidth / 2 + tankMargin}
                                        cy={tankHeight * 0.9}
                                        rx={(tankWidth / 2) - tankpadding}
                                        ry={tankHeight * 0.1 - tankpadding * 0.5}
                                        fill={fillColor}
                                        stroke={fillColor}
                                        strokeWidth={0}
                                    />
                                    {/* Liquid level */}
                                    <rect 
                                        x={tankMargin + tankpadding} 
                                        y={(tankMargin + tankHeight * 0.285) + (tankHeight * 0.65 * (100 - fillHeight) / 100)} 
                                        width={tankWidth - tankpadding * 2}
                                        //height={tankHeight * 0.61 * (fillHeight / 100) - tankpadding}
                                        height={(tankHeight * 0.9) - ((tankMargin + tankHeight * 0.285) + (tankHeight * 0.65 * (100 - fillHeight) / 100))}
                                        fill={fillColor}
                                    />
                                    {/* Liquid level ellipse */}
                                    <ellipse
                                        cx={tankWidth / 2 + tankMargin}
                                        cy={(tankMargin + tankHeight * 0.285) + (tankHeight * 0.65 * (100 - fillHeight) / 100)}
                                        rx={(tankWidth / 2) - tankpadding}
                                        ry={tankHeight * 0.1 - tankpadding * 0.5}
                                        fill={fillColor}
                                        stroke={fillColor}
                                        strokeWidth={0}
                                    />
                                    {/* Liquid level ellipse indicator*/}
                                    <ellipse
                                        cx={tankWidth / 2 + tankMargin}
                                        cy={(tankMargin + tankHeight * 0.285) + (tankHeight * 0.65 * (100 - fillHeight) / 100)}
                                        rx={(tankWidth / 2) - tankpadding}
                                        ry={tankHeight * 0.1 - tankpadding * 0.5}
                                        fill='white'
                                        stroke={fillColor}
                                        strokeWidth={0}
                                        opacity={0.3}
                                    />
                                </svg>
                            </div>
                        </div>
                        <div className="vertical-cylinder-values-display-container">
                            {widget?.config?.data?.displayType === 'actual'?
                                (
                                    <div className="vertical-cylinder-absolute-values-display-container">
                                        <div className="values-container">
                                            <div className="numerator">
                                                <p>{currentLevel}</p>
                                            </div>
                                            <div className="denominator">
                                                <p>{maxLevel}</p>
                                            </div>
                                        </div>
                                        <div className="units-container">
                                            <p>{widget?.config?.data?.units?.symbol}</p>
                                        </div>
                                    </div>
                                ):(
                                    <div className="vertical-cylinder-percentage-values-display-container">
                                        <p>{((currentLevel / (maxLevel?? 1)) * 100).toFixed(2)}%</p>
                                    </div>
                                )
                            }
                        </div>
                    </div>
                </div>
            </Resizable>
        </div>
    );
};

export default VerticalCylinderTankWidget;